import { getAuthUrl } from '../config/auth';
import { apiService } from './api';
import { tokenRefreshService, TokenResponse } from './tokenRefresh';

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

class AuthService {
  private initializationPromise: Promise<void> | null = null;

  constructor() {
    // Listen for session expiration events
    window.addEventListener('auth:session-expired', () => {
      this.handleSessionExpired();
    });
  }

  async initialize(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = (async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      const expiresAt = localStorage.getItem('expiresAt');

      if (refreshToken && expiresAt) {
        const timeUntilExpiry = parseInt(expiresAt) - Date.now();

        // If token expires in less than 5 minutes, refresh immediately
        if (timeUntilExpiry < 5 * 60 * 1000) {
          try {
            await this.refreshSession();
          } catch (error) {
            console.error('Failed to refresh session on init:', error);
            this.logout();
          }
        } else {
          // Schedule automatic refresh
          const expiresIn = Math.floor(timeUntilExpiry / 1000);
          tokenRefreshService.scheduleTokenRefresh(
            expiresIn,
            refreshToken,
            (tokens) => this.handleTokenRefresh(tokens)
          );
        }
      }
    })();

    return this.initializationPromise;
  }

  login() {
    window.location.href = getAuthUrl('login');
  }

  logout() {
    tokenRefreshService.cancelTokenRefresh();
    apiService.clearIdToken();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiresAt');
    window.location.href = getAuthUrl('logout');
  }

  async handleCallback(): Promise<boolean> {
    // Check for authorization code (code flow)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      try {
        // Exchange code for tokens
        const tokens = await tokenRefreshService.exchangeCodeForTokens(code);
        this.storeTokens(tokens);

        // Clean up URL
        window.history.replaceState(null, '', window.location.pathname);

        return true;
      } catch (error) {
        console.error('Failed to exchange authorization code:', error);
        return false;
      }
    }

    return false;
  }

  private storeTokens(tokens: TokenResponse) {
    // Store tokens
    apiService.setIdToken(tokens.id_token);
    localStorage.setItem('accessToken', tokens.access_token);

    if (tokens.refresh_token) {
      localStorage.setItem('refreshToken', tokens.refresh_token);
    }

    const expiresAt = Date.now() + tokens.expires_in * 1000;
    localStorage.setItem('expiresAt', expiresAt.toString());

    // Schedule automatic token refresh
    const refreshToken = tokens.refresh_token || localStorage.getItem('refreshToken');
    if (refreshToken) {
      tokenRefreshService.scheduleTokenRefresh(
        tokens.expires_in,
        refreshToken,
        (newTokens) => this.handleTokenRefresh(newTokens)
      );
    }
  }

  private handleTokenRefresh(tokens: TokenResponse) {
    console.log('Tokens refreshed successfully');
    this.storeTokens(tokens);
  }

  private handleSessionExpired() {
    console.warn('Session expired - redirecting to login');
    this.logout();
  }

  async refreshSession(): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const tokens = await tokenRefreshService.refreshTokens(refreshToken);
    this.storeTokens(tokens);
  }

  isAuthenticated(): boolean {
    const idToken = apiService.getIdTokenFromStorage();
    const refreshToken = localStorage.getItem('refreshToken');

    // As long as we have a refresh token, we can maintain the session
    return !!(idToken || refreshToken);
  }

  isAdmin(): boolean {
    const userInfo = this.getUserInfo();
    if (!userInfo) return false;

    // 1. Check Cognito groups
    const groups = userInfo['cognito:groups'];
    if (Array.isArray(groups) && groups.includes('Admins')) return true;
    if (typeof groups === 'string' && groups.split(',').includes('Admins')) return true;

    // 2. Check development environment override list
    const email = userInfo.email;
    const adminEmailsEnv = import.meta.env.VITE_ADMIN_EMAILS || '';
    if (email && adminEmailsEnv) {
      const adminEmails = adminEmailsEnv.split(',').map((e: string) => e.trim().toLowerCase());
      return adminEmails.includes(email.toLowerCase());
    }

    return false;
  }

  getIdToken(): string | null {
    return localStorage.getItem('idToken');
  }

  getUserInfo(): any {
    const idToken = this.getIdToken();
    if (!idToken) return null;

    try {
      // Decode JWT (simple base64 decode, no verification)
      const payload = idToken.split('.')[1];
      
      // Convert Base64URL to standard Base64
      let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      
      // Add padding if missing
      const pad = base64.length % 4;
      if (pad) {
        if (pad === 1) {
          throw new Error('Invalid base64 string');
        }
        base64 += new Array(5 - pad).join('=');
      }

      const decoded = JSON.parse(atob(base64));
      return decoded;
    } catch (error) {
      console.error('Failed to decode ID token:', error);
      return null;
    }
  }
}

export const authService = new AuthService();
